import React, { useState } from 'react';
import { evaluateResistanceAlerts } from '../utils/microbiologyTemplateEngine';

export default function CommentsLibrarySection({
  data,
  commentTemplates = [],
  onChangeRemarks,
  onSaveRemarks
}) {
  const [activeCategory, setActiveCategory] = useState('ALL');
  const [commentSearch, setCommentSearch] = useState('');
  const [saving, setSaving] = useState(false);

  // Auto-generate comprehensive clinical interpretation statement
  const buildAutoInterpretation = () => {
    if (!data) return 'Pending case compilation...';

    if (data.cultureStatus === 'No Growth') {
      return `No aerobic bacterial growth detected after standard 48 hours incubation from ${data.specimenName || 'the submitted specimen'}. Direct smear did not reveal significant cellular reaction. Suggest clinical correlation.`;
    }

    if (data.cultureStatus === 'Pending') {
      return `Culture plates are actively incubating (${data.daysInIncubation || 1} day elapsed). Interim direct microscopy and preliminary readings documented. Final report pending incubation completion.`;
    }

    const isolates = data.isolates || [];
    if (isolates.length === 0) {
      return `Culture growth recorded (${data.colonyCount || 'positive'}). Organism identification and antimicrobial susceptibility testing in technical processing.`;
    }

    const alerts = isolates.flatMap((i) => evaluateResistanceAlerts(i));
    const lines = [];

    lines.push(
      `Culture yielded ${data.growthSignificance || 'significant pure growth'} of ${isolates
        .map((i) => `${i.organismName} (${i.colonyMorphology || i.quantityDescription || 'Heavy growth'})`)
        .join(' and ')} from ${data.specimenName}.`
    );

    isolates.forEach((iso, idx) => {
      const rDrugs = (iso.astResults || []).filter((r) => r.finalInterpretation === 'R');
      const sDrugs = (iso.astResults || []).filter((r) => r.finalInterpretation === 'S');

      if (rDrugs.length > 0) {
        lines.push(
          `Isolate #${idx + 1} (${iso.organismName}) demonstrated resistance to: ${rDrugs.map((d) => d.drugName).join(', ')}.`
        );
      }
      if (sDrugs.length > 0) {
        lines.push(
          `Susceptible antimicrobials include: ${sDrugs.map((d) => d.drugName).join(', ')}.`
        );
      }
    });

    if (alerts.length > 0) {
      lines.push(
        `Resistance Surveillance Alert: ${alerts.map((a) => a.name).join('; ')}. Infection control precautions recommended.`
      );
    }

    return lines.join('\n\n');
  };

  const autoInterpretation = buildAutoInterpretation();

  // Categories for comments
  const standardCategories = ['ALL', 'Culture', 'Microscopy', 'AST', 'Resistance', 'Specimen Quality', 'Clinical'];

  // Filter templates
  const filteredTemplates = commentTemplates.filter((c) => {
    const matchesCat = activeCategory === 'ALL' || (c.category || 'Clinical') === activeCategory;
    const matchesSearch =
      !commentSearch ||
      (c.title && c.title.toLowerCase().includes(commentSearch.toLowerCase())) ||
      (c.commentText && c.commentText.toLowerCase().includes(commentSearch.toLowerCase()));
    return matchesCat && matchesSearch;
  });

  const handleInsertComment = (text) => {
    const current = data?.finalRemarks || '';
    const newText = current ? `${current}\n\n• ${text}`.trim() : `• ${text}`;
    onChangeRemarks(newText);
  };

  const handleAdoptAutoInterpretation = () => {
    const current = data?.finalRemarks || '';
    const newText = current ? `${current}\n\n${autoInterpretation}`.trim() : autoInterpretation;
    onChangeRemarks(newText);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSaveRemarks();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wb-stage-view">
      {/* Header */}
      <div className="wb-stage-header">
        <div className="wb-stage-title-wrap">
          <h3 className="wb-stage-title">Clinical Interpretive Remarks</h3>
          <span className="wb-stage-subtitle">
            Auto-synthesized interpretation &bull; Searchable comment library &bull; Custom microbiologist notes
          </span>
        </div>
      </div>

      {/* Auto-Generated Clinical Interpretation Card */}
      <div className="wb-card-clean" style={{ background: '#f8fafc', marginBottom: '16px' }}>
        <div className="wb-card-clean-header">
          <div>
            <h4 className="wb-card-clean-title" style={{ color: '#0369a1' }}>
              Auto-Synthesized Diagnostic Summary
            </h4>
            <span className="wb-hint-text">
              Dynamically composed from culture findings, identified isolates, AST results, and expert rules.
            </span>
          </div>

          <button
            type="button"
            className="wb-btn-secondary-sm"
            onClick={handleAdoptAutoInterpretation}
            title="Adopt this generated statement into official report remarks"
          >
            📋 Insert into Remarks &darr;
          </button>
        </div>

        <div className="wb-auto-summary-text">
          {autoInterpretation}
        </div>
      </div>

      {/* 2-Column: Comment Library + Manual Remarks */}
      <div className="wb-clinical-form-layout">
        {/* Left Column: Comment Library */}
        <div className="wb-form-column">
          <div className="wb-card-clean" style={{ height: '100%' }}>
            <div className="wb-card-clean-header">
              <div>
                <h4 className="wb-card-clean-title">Comment Library</h4>
                <span className="wb-hint-text">Standard pre-approved clinical comments</span>
              </div>
            </div>

            {/* Filter Bar */}
            <div className="wb-comment-filter-bar">
              <input
                type="text"
                placeholder="Search templates..."
                className="wb-input-search"
                value={commentSearch}
                onChange={(e) => setCommentSearch(e.target.value)}
              />

              <select
                className="wb-select-compact"
                value={activeCategory}
                onChange={(e) => setActiveCategory(e.target.value)}
              >
                {standardCategories.map((c) => (
                  <option key={c} value={c}>
                    {c === 'ALL' ? 'All Categories' : c}
                  </option>
                ))}
              </select>
            </div>

            {/* Comment List */}
            <div className="wb-comment-list-scroll">
              {filteredTemplates.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', fontSize: '0.82rem' }}>
                  No comment templates found.
                </div>
              ) : (
                filteredTemplates.map((tmpl) => (
                  <div key={tmpl.commentID} className="wb-comment-item">
                    <div className="wb-comment-item-header">
                      <div>
                        <span className="wb-tag-category">{tmpl.category || 'Clinical'}</span>
                        <strong className="wb-comment-item-title">{tmpl.title}</strong>
                      </div>
                      <button
                        type="button"
                        className="wb-btn-insert"
                        onClick={() => handleInsertComment(tmpl.commentText)}
                      >
                        + Insert
                      </button>
                    </div>
                    <p className="wb-comment-item-text">{tmpl.commentText}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Microbiologist Remarks Textarea */}
        <div className="wb-form-column">
          <div className="wb-card-clean" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="wb-card-clean-header">
              <div>
                <h4 className="wb-card-clean-title">Official Report Remarks & Guidance</h4>
                <span className="wb-hint-text">
                  This text will print directly on the official diagnostic report.
                </span>
              </div>
              <span className="wb-char-counter">
                {(data?.finalRemarks || '').length} chars
              </span>
            </div>

            <textarea
              rows="10"
              className="wb-textarea"
              style={{ flex: 1, minHeight: '220px' }}
              placeholder="Enter official clinical remarks, therapeutic guidance, and doctor's interpretive notes..."
              value={data?.finalRemarks || ''}
              onChange={(e) => onChangeRemarks(e.target.value)}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
              <button
                type="button"
                className="wb-btn-primary"
                disabled={saving}
                onClick={handleSave}
              >
                {saving ? 'Saving...' : 'Save Remarks'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
